import React, { useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import { loadDamageAnimation } from './animationLoader';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * DamageAnimation component - renders a single damage explosion animation
 * @param {Object} props
 * @param {number} props.x - X position offset (0-100, percentage)
 * @param {number} props.y - Y position offset (0-100, percentage)
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {number} props.animationKey - Unique key for this animation instance
 * @param {number} props.animationSpeedMultiplier - Speed multiplier (1 = normal, 0.5 = 2x slower)
 */
export const DamageAnimation = ({ x, y, onComplete, animationKey, animationSpeedMultiplier = 1 }) => {
  const lottieRef = useRef(null);
  const [animationData, setAnimationData] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  // Load animation data once (shared across all instances)
  useEffect(() => {
    loadDamageAnimation().then(data => {
      if (data) {
        console.log('✅ DamageAnimation: Animation data loaded');
        setAnimationData(data);
      } else {
        console.error('❌ DamageAnimation: Failed to load animation data');
      }
    }).catch(error => {
      console.error('❌ DamageAnimation: Error loading animation data', error);
    });
  }, []);

  // Reset and play animation when key changes
  useEffect(() => {
    if (!animationKey || !animationData) return;
    
    console.log('🎬 DamageAnimation: Starting animation', { animationKey, x, y, animationSpeedMultiplier });
    setIsPlaying(true);
    
    // ✅ Play damage sound when animation starts
    try {
      playSoundEffect('/sounds/damage.mp3', 0.6); // Play at 60% volume
      console.log('🔊 Playing damage sound for damage animation');
    } catch (error) {
      console.error('❌ Error playing damage sound:', error);
    }
    
    // Reset animation to beginning when key changes (autoplay will start it)
    if (lottieRef.current) {
      setTimeout(() => {
        if (lottieRef.current) {
          try {
            // ✅ SPEED CONTROL: Set animation speed based on multiplier
            // At 100% speed (multiplier=1): normal speed
            // At 50% speed (multiplier=0.5): 0.5x speed (2x slower)
            const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
            lottieRef.current.setSpeed(speedFactor);
            console.log(`⏩ DamageAnimation: Setting lottie speed to ${speedFactor}`);
            
            // Force reset to beginning and play
            lottieRef.current.goToAndStop(0, true);
            lottieRef.current.play();
          } catch (error) {
            console.error('❌ DamageAnimation: Error resetting animation', error);
          }
        }
      }, 10);
    }
  }, [animationKey, animationData, x, y, animationSpeedMultiplier]); // Depend on all relevant props

  if (!animationData) {
    console.log('⏳ DamageAnimation: Waiting for animation data to load', { animationKey, x, y });
    return null; // Don't render until animation is loaded
  }

  // Use percentage positioning (relative to parent container)
  const left = `${x}%`;
  const top = `${y}%`;

  return (
    <div
      style={{
        position: 'absolute',
        left, // Percentage-based positioning
        top,
        width: '200px', // Increased size for visibility
        height: '200px',
        transform: 'translate(-50%, -50%)', // Center on the position
        zIndex: 10000, // Very high z-index to ensure it's visible above everything
        pointerEvents: 'none',
        backgroundColor: 'transparent',
        opacity: 1,
        willChange: 'transform', // Performance hint
      }}
      data-animation-key={animationKey}
      data-debug-position={`percent:${x},${y}`}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={false}
        autoplay={true}
        onComplete={() => {
          // Callback when animation completes
          setIsPlaying(false);
          if (onComplete) {
            onComplete();
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          opacity: 1,
        }}
      />
    </div>
  );
};

export default DamageAnimation;

